import { BaseTable, Card, FooterMenu, HeaderMenu, Input, Loading, PaginationData, Select, TableHeader, TableNumber } from "components";
import { debounce } from "lodash";
import { useProductCategory } from "pages/Inventori/Register/utils";
import { useState } from "react";
import { convertNumber, default_pagination_value } from "utils";
import { FilteredParams, useList } from "./utils";
import { PicturesSection } from "pages/Inventori/Register/ItemGroup/components";
import { Detail } from "pages/Inventori/Transaction/AddNewItem/components/ChildItem/components/Item/components";


export default function PricingAnalyse() {

    const { dataCategory, isLoadingCategory } = useProductCategory();

    const [pagination, setPagination] = useState<FilteredParams>(
        default_pagination_value
    );
    const { data, isLoading } = useList(pagination);

    const onDebounce = debounce((e: any) => {
        setPagination((prev) => {
            return {
                ...prev,
                keyword: e.target.value,
                page: 1,
                group_id: "",
            };
        });
    })

    const averageLastPrice = data.items.reduce((accumulator, item) => accumulator + Number(item.last_price), 0) / data.items.length
    const averageAvgPrice = data.items.reduce((accumulator, item) => accumulator + Number(item.avg_price), 0) / data.items.length
    const averageCac = data.items.reduce((accumulator, item) => accumulator + Number(item.cac), 0) / data.items.length
    const averageCogm = data.items.reduce((accumulator, item) => accumulator + Number(item.cogm), 0) / data.items.length
    const averageCogmPercentage = data.items.reduce((accumulator, item) => accumulator + Number(item.cogm_percent), 0) / data.items.length
    const averagePriceRetail = data.items.reduce((accumulator, item) => accumulator + Number(item.price_retail), 0) / data.items.length
    const averagePriceRetailPercentage = data.items.reduce((accumulator, item) => accumulator + Number(item.profit_percent), 0) / data.items.length
    const averagePriceWholesale = data.items.reduce((accumulator, item) => accumulator + Number(item.wholesales), 0) / data.items.length
    const averagePriceWholesalePercentage = data.items.reduce((accumulator, item) => accumulator + Number(item.profit_wholesales_percent), 0) / data.items.length
    const averageDistributor = data.items.reduce((accumulator, item) => accumulator + Number(item.distributor_price), 0) / data.items.length
    const averageDistributorPercentage = data.items.reduce((accumulator, item) => accumulator + Number(item.profit_distributor_percent), 0) / data.items.length
    const averageFactory = data.items.reduce((accumulator, item) => accumulator + Number(item.factory_price), 0) / data.items.length
    const averageFactoryPercentage = data.items.reduce((accumulator, item) => accumulator + Number(item.profit_factory_percent), 0) / data.items.length
    const averageOnline = data.items.reduce((accumulator, item) => accumulator + Number(item.online_price), 0) / data.items.length
    const averageOnlinePercentage = data.items.reduce((accumulator, item) => accumulator + Number(item.profit_online_percent), 0) / data.items.length

    return (
        <section className="container my-5">
            <HeaderMenu title="PRICING ANALYSE" className="sticky top-[32px] z-[1]" />
            <div className="my-2">
                <section className="mt-2">
                    <Card>
                        <Card.Body>
                            <div className="lg:flex lg:justify-between items-center">
                                <Input
                                    label="SEARCH"
                                    className="lg:w-[300px] md:w-full"
                                    placeholder="Search data..."
                                    onChange={onDebounce}
                                />

                                <Select
                                    label="PRODUCT CATEGORY"
                                    placeholder="Select product category here..."
                                    className="lg:w-[200px] w-full me-2 mb-2"
                                    width={true}
                                    defaultValue={{ value: "0", label: "All" }}
                                    isLoading={isLoadingCategory}
                                    options={[{ label: "All", value: "" }].concat(
                                        dataCategory
                                    )}
                                    value={dataCategory.find(
                                        (item) => item.value === pagination?.group_id
                                    )}
                                    onChange={(e) =>
                                        setPagination({
                                            ...pagination,
                                            group_id: e?.value ?? "",
                                            page: 1,
                                        })
                                    }
                                />
                            </div>
                        </Card.Body>
                    </Card>
                </section>

                <section className="my-2">
                    <Card>
                        <Card.Body>
                            <div className="overflow-x-auto max-h-[calc(100vh-600px)]">
                                <BaseTable className="border-collapse">
                                    <TableHeader>
                                        <tr>
                                            <th>NO</th>
                                            <th>PICTURE</th>
                                            <th>BSI</th>
                                            <th>CODE</th>
                                            <th>DESCRIPTION</th>
                                            <th>LAST PRICE</th>
                                            <th>AVG PRICE</th>
                                            <th>CAC</th>
                                            <th>COGM</th>
                                            <th>COGM(%)</th>
                                            <th>RETAIL</th>
                                            <th>PROFIT(%)</th>
                                            <th>WHOLESALE</th>
                                            <th>PROFIT(%)</th>
                                            <th>DISTRIBUTOR</th>
                                            <th>PROFIT(%)</th>
                                            <th>FACTORY</th>
                                            <th>PROFIT(%)</th>
                                            <th>ONLINE(%)</th>
                                            <th>PROFIT(%)</th>
                                            <th>ACTION</th>
                                        </tr>
                                    </TableHeader>

                                    <tbody>
                                        {isLoading ?
                                            <tr>
                                                <td
                                                    colSpan={20}
                                                    className="text-center"
                                                >
                                                     <Loading errorText="No data available" loading={isLoading} />
                                                </td>
                                            </tr>
                                            : data.items.map((item: ReturnType<typeof useList>["data"]["items"][number], index) => {
                                                return (
                                                    <tr>
                                                        <td>
                                                            {TableNumber({
                                                                dataLength: data.items.length,
                                                                pageCurrent: pagination.page,
                                                                index: index,
                                                                limit: pagination.limit,
                                                                totalData: pagination.total,
                                                            })}
                                                        </td>
                                                        <td className="!w-[80px]">
                                                            <div className="flex justify-center">
                                                                {Boolean(item?.pictures) && (
                                                                    <PicturesSection
                                                                        dataPicture={item?.pictures}
                                                                        items={1}
                                                                        className="size-[50px] my-[-10px]"
                                                                    />
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="text-center">{`${item.buy ? "B" : ""}${item.sell ? "S" : ""}${item.inventory ? "I" : ""}`}</td>
                                                        <td>{item.product_barcode}</td>
                                                        <td>{item.product_name}</td>
                                                        <td className="text-right">{item.last_price ? convertNumber(Number(item.last_price)).intoCurrency : "0"}</td>
                                                        <td className="text-right"> {item.avg_price ? convertNumber(Number(item.avg_price)).intoCurrency : "0"}</td>
                                                        <td className="text-right">{item.cac ? convertNumber(Number(item.cac)).intoCurrency : "0"}</td>
                                                        <td className="text-right text-red-500">{item.cogm ? convertNumber(Number(item.cogm)).intoCurrency : "0"}</td>
                                                        <td className="text-center">{item.cogm_percent ? convertNumber(Number(item.cogm_percent)).intoNormalAmount : "0"} %</td>
                                                        <td className="text-right">{item.price_retail ? convertNumber(Number(item.price_retail)).intoCurrency : "0"}</td>
                                                        <td className="text-center">{item.profit_percent ? convertNumber(Number(item.profit_percent)).intoNormalAmount : "0"} %</td>
                                                        <td className="text-right">{item.wholesales ? convertNumber(Number(item.wholesales)).intoCurrency : "0"}</td>
                                                        <td className="text-center">{item.profit_wholesales_percent ? convertNumber(Number(item.profit_wholesales_percent)).intoNormalAmount : "0"} %</td>
                                                        <td className="text-right">{item.distributor_price ? convertNumber(Number(item.distributor_price)).intoCurrency : "0"}</td>
                                                        <td className="text-center">{item.profit_distributor_percent ? convertNumber(Number(item.profit_distributor_percent)).intoNormalAmount : "0"} %</td>
                                                        <td className="text-right">{item.factory_price ? convertNumber(Number(item.factory_price)).intoCurrency : "0"}</td>
                                                        <td className="text-center">{item.profit_factory_percent ? convertNumber(Number(item.profit_factory_percent)).intoNormalAmount : "0"} %</td>
                                                        <td className="text-right">{item.online_price ? convertNumber(Number(item.online_price)).intoCurrency : "0"}</td>
                                                        <td className="text-center">{item.profit_online_percent ? convertNumber(Number(item.profit_online_percent)).intoNormalAmount : "0"} %</td>
                                                        <td>
                                                            <Detail id={Number(item.product_id)} />
                                                        </td>
                                                    </tr>
                                                )
                                            })
                                        }
                                    </tbody>

                                    <tfoot className="bg-black text-white font-bold">
                                        <tr>
                                            <th colSpan={5} className="text-right">AVERAGE</th>
                                            <th className="text-right">{convertNumber(averageLastPrice).intoCurrency}</th>
                                            <th className="text-right">{convertNumber(averageAvgPrice).intoCurrency}</th>
                                            <th className="text-right">{convertNumber(averageCac).intoCurrency}</th>
                                            <th className="text-right">{convertNumber(averageCogm).intoCurrency}</th>
                                            <th className="text-center">{convertNumber(averageCogmPercentage).intoNormalAmount} %</th>
                                            <th className="text-right">{convertNumber(averagePriceRetail).intoCurrency}</th>
                                            <th className="text-center">{convertNumber(averagePriceRetailPercentage).intoNormalAmount} %</th>
                                            <th className="text-right">{convertNumber(averagePriceWholesale).intoCurrency}</th>
                                            <th className="text-center">{convertNumber(averagePriceWholesalePercentage).intoNormalAmount} %</th>
                                            <th className="text-right">{convertNumber(averageDistributor).intoCurrency}</th>
                                            <th className="text-center">{convertNumber(averageDistributorPercentage).intoNormalAmount} %</th>
                                            <th className="text-right">{convertNumber(averageFactory).intoCurrency}</th>
                                            <th className="text-center">{convertNumber(averageFactoryPercentage).intoNormalAmount} %</th>
                                            <th className="text-right">{convertNumber(averageOnline).intoCurrency}</th>
                                            <th className="text-center">{convertNumber(averageOnlinePercentage).intoNormalAmount} %</th>
                                            <th></th>
                                        </tr>
                                    </tfoot>
                                </BaseTable>
                            </div>
                            <div className="mt-5">
                                    <PaginationData
                                        data={data}
                                        pagination={pagination}
                                        setPagination={setPagination}
                                    />
                                </div>
                        </Card.Body>
                    </Card>
                </section>

                <section>
                    <FooterMenu
                        export={{
                            actiontype: "export"
                        }}

                        pdf={{
                            actiontype: "pdf",
                        }}
                    />
                </section>
            </div>
        </section>
    )
}